import validator from 'validator';

/**
 * A collection of security-focused validation and sanitization utilities.
 */
export const securityValidators = {
  /**
   * Sanitizes a string to prevent XSS attacks.
   * It escapes HTML entities, trims whitespace, and truncates the string.
   * @param input The string to sanitize.
   * @param maxLength The maximum allowed length of the string. Defaults to 256.
   * @returns The sanitized string, or an empty string if the input is not a string.
   */
  sanitizeInput: (input: unknown, maxLength: number = 256): string => {
    if (typeof input !== 'string') {
      return '';
    }
    const sanitized = validator.escape(input.trim());
    return sanitized.substring(0, maxLength);
  },

  /**
   * Validates if a string is a well-formed email address.
   * @param email The email string to validate.
   * @returns True if the email is valid, false otherwise.
   */
  isValidEmail: (email: string): boolean => {
    return validator.isEmail(email);
  },

  /**
   * Validates if a string is a well-formed URL.
   * @param url The URL string to validate.
   * @returns True if the URL is valid, false otherwise.
   */
  isValidUrl: (url: string): boolean => {
    return validator.isURL(url);
  },

  /**
   * Detects common XSS attack patterns in a string.
   * This provides a more aggressive check than sanitization alone.
   * @param input The string to check for XSS patterns.
   * @returns True if a potential XSS pattern is detected, false otherwise.
   */
  detectXSS: (input: string): boolean => {
    const xssPatterns = [
      /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
      /javascript:/gi,
      /vbscript:/gi,
      /onload\s*=/gi,
      /onerror\s*=/gi,
      /onclick\s*=/gi,
      /onmouseover\s*=/gi,
      /<iframe/gi,
      /<object/gi,
      /<embed/gi,
    ];

    return xssPatterns.some(pattern => pattern.test(input));
  },
};
